import { useEffect, useReducer } from "react";
import {
  createEffectInstance,
  effectRegistry,
  type AlterlyEffectId,
} from "../../core/effects/effectRegistry";
import {
  duplicateEffect,
  moveEffect,
  removeEffect,
  setEffectOpacity,
} from "../../core/effects/effectStack";
import type { HalftoneParameters } from "../../core/effects/halftone";
import type { NoiseParameters } from "../../core/effects/noise";
import {
  createEffectStackHistoryState,
  effectStackHistoryReducer,
  type EffectStackSnapshot,
} from "../../core/project/effectStackHistory";

function createInitialSnapshot(): EffectStackSnapshot {
  const initialEffect = createEffectInstance("halftone");
  return {
    effects: [initialEffect],
    selectedId: initialEffect.instanceId,
  };
}

export function useEffectStack() {
  const [history, dispatch] = useReducer(
    effectStackHistoryReducer,
    undefined,
    () => createEffectStackHistoryState(createInitialSnapshot()),
  );
  const { effects, selectedId } = history.present;
  const selected = effects.find((effect) => effect.instanceId === selectedId) ?? effects[0] ?? null;

  const commit = (snapshot: EffectStackSnapshot) => dispatch({ type: "commit", snapshot });

  const add = (definitionId: AlterlyEffectId) => {
    const effect = createEffectInstance(definitionId);
    commit({ effects: [...effects, effect], selectedId: effect.instanceId });
  };

  const select = (instanceId: string) => dispatch({ type: "select", selectedId: instanceId });

  const toggle = (instanceId: string) => {
    commit({
      effects: effects.map((effect) => (
        effect.instanceId === instanceId ? { ...effect, enabled: !effect.enabled } : effect
      )),
      selectedId,
    });
  };

  const updateOpacity = (instanceId: string, opacity: number) => {
    commit({ effects: setEffectOpacity(effects, instanceId, opacity), selectedId });
  };

  const duplicate = (instanceId: string) => {
    const next = duplicateEffect(effects, instanceId);
    const index = next.findIndex((effect) => effect.instanceId === instanceId);
    const duplicated = next[index + 1];
    commit({
      effects: next,
      selectedId: duplicated?.instanceId ?? selectedId,
    });
  };

  const remove = (instanceId: string) => {
    const index = effects.findIndex((effect) => effect.instanceId === instanceId);
    const next = removeEffect(effects, instanceId);
    const nextSelectedId = selectedId === instanceId
      ? next[Math.min(Math.max(index, 0), Math.max(next.length - 1, 0))]?.instanceId ?? null
      : selectedId;
    commit({ effects: next, selectedId: nextSelectedId });
  };

  const move = (instanceId: string, direction: -1 | 1) => {
    commit({ effects: moveEffect(effects, instanceId, direction), selectedId });
  };

  const updateHalftone = (instanceId: string, patch: Partial<HalftoneParameters>) => {
    commit({
      effects: effects.map((effect) => (
        effect.instanceId === instanceId && effect.definitionId === "halftone"
          ? { ...effect, parameters: { ...effect.parameters, ...patch } }
          : effect
      )),
      selectedId,
    });
  };

  const updateNoise = (instanceId: string, patch: Partial<NoiseParameters>) => {
    commit({
      effects: effects.map((effect) => (
        effect.instanceId === instanceId && effect.definitionId === "noise"
          ? { ...effect, parameters: { ...effect.parameters, ...patch } }
          : effect
      )),
      selectedId,
    });
  };

  const resetSelected = () => {
    if (!selected) return;
    commit({
      effects: effects.map((effect) => {
        if (effect.instanceId !== selected.instanceId) return effect;
        if (effect.definitionId === "halftone") {
          return { ...effect, parameters: { ...effectRegistry.halftone.defaults }, opacity: 1 };
        }
        return { ...effect, parameters: { ...effectRegistry.noise.defaults }, opacity: 1 };
      }),
      selectedId,
    });
  };

  const undo = () => dispatch({ type: "undo" });
  const redo = () => dispatch({ type: "redo" });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier || event.key.toLowerCase() !== "z") return;
      event.preventDefault();
      dispatch({ type: event.shiftKey ? "redo" : "undo" });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return {
    effects,
    selected,
    add,
    select,
    toggle,
    updateOpacity,
    duplicate,
    remove,
    move,
    updateHalftone,
    updateNoise,
    resetSelected,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
}
